import { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import axios from 'axios';
import { 
  MapPin, 
  Calendar, 
  Users, 
  Loader2,
  ChevronLeft,
  Trophy,
  Award,
  ExternalLink
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const TravelGuideDetailPage = () => {
  const { stateCode } = useParams();
  const [guide, setGuide] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetch = async () => {
      try {
        const res = await axios.get(`${API_URL}/travel-guides/${stateCode}`);
        setGuide(res.data);
      } catch (err) {
        console.error('Failed to fetch guide:', err);
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, [stateCode]);

  if (loading) {
    return (
      <div className="min-h-screen bg-bone pt-24 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-fairway" />
      </div>
    );
  }

  if (!guide) {
    return (
      <div className="min-h-screen bg-bone pt-24 flex items-center justify-center">
        <p className="text-muted-foreground">State not found</p>
      </div>
    );
  }

  const hasCourses = guide.courses?.length > 0;
  const hasEvents = guide.events?.length > 0;
  const hasLeagues = guide.leagues?.length > 0;
  const hasPros = guide.pros?.length > 0;
  const hasContent = hasCourses || hasEvents || hasLeagues || hasPros;

  return (
    <div className="min-h-screen bg-bone pt-24 pb-20">
      {/* Header */}
      <div className="bg-deep-green py-12 mb-8">
        <div className="container-padding">
          <Link to="/travel-guides" className="inline-flex items-center gap-1 text-cream/60 hover:text-gold text-sm mb-4 transition-colors">
            <ChevronLeft className="w-4 h-4" /> All Travel Guides
          </Link>
          <h1 className="font-display font-bold text-3xl md:text-4xl text-white mb-3" data-testid="guide-state-title">
            {guide.state_name} Golf Guide
          </h1>
          <div className="flex flex-wrap gap-4 text-cream/70 text-sm">
            <span className="flex items-center gap-1"><MapPin className="w-4 h-4 text-gold" /> {guide.courses?.length || 0} courses ({guide.minority_courses} minority-owned)</span>
            <span className="flex items-center gap-1"><Calendar className="w-4 h-4 text-gold" /> {guide.events?.length || 0} upcoming events</span>
            <span className="flex items-center gap-1"><Users className="w-4 h-4 text-gold" /> {guide.leagues?.length || 0} leagues</span>
            <span className="flex items-center gap-1"><Award className="w-4 h-4 text-gold" /> {guide.pros?.length || 0} pros</span>
          </div>
        </div>
      </div>

      <div className="container-padding">
        {!hasContent && (
          <div className="text-center py-16">
            <MapPin className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <h2 className="font-display font-bold text-xl text-deep-green mb-2">Coming Soon</h2>
            <p className="text-muted-foreground mb-4">We're building the {guide.state_name} golf directory. Know a course or event? Submit it!</p>
            <Link to="/calendar"><Button className="btn-gold">Submit an Event</Button></Link>
          </div>
        )}

        {/* Courses */}
        {hasCourses && (
          <section className="mb-10">
            <h2 className="font-display font-bold text-xl text-deep-green mb-4 flex items-center gap-2">
              <MapPin className="w-5 h-5 text-gold" /> Golf Courses ({guide.courses.length})
            </h2>
            <div className="grid md:grid-cols-2 gap-4">
              {guide.courses.map(course => (
                <Link key={course.course_id || course.id} to={`/courses/${course.course_id || course.id}`}>
                  <Card className="bg-white border-0 shadow-sm hover:shadow-md transition-all h-full">
                    <CardContent className="p-5">
                      <div className="flex items-start justify-between mb-2">
                        <h3 className="font-display font-bold text-deep-green hover:text-gold transition-colors">{course.name}</h3>
                        {course.minority_owned && <Badge className="bg-gold/20 text-gold text-xs shrink-0">Minority-Owned</Badge>}
                      </div>
                      <p className="text-muted-foreground text-sm mb-2">{course.city}, {course.state}</p>
                      {course.pricing_notes && <p className="text-xs text-muted-foreground line-clamp-2">{course.pricing_notes}</p>}
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          </section>
        )}

        {/* Events */}
        {hasEvents && (
          <section className="mb-10">
            <h2 className="font-display font-bold text-xl text-deep-green mb-4 flex items-center gap-2">
              <Calendar className="w-5 h-5 text-gold" /> Upcoming Events ({guide.events.length})
            </h2>
            <div className="space-y-3">
              {guide.events.map(event => {
                const start = new Date(event.start_at);
                return (
                  <Link key={event.event_id || event.id} to={`/events/${event.event_id || event.id}`}>
                    <Card className="bg-white border-0 shadow-sm hover:shadow-md transition-all mb-3">
                      <CardContent className="p-4 flex items-center gap-4">
                        <div className="w-14 text-center bg-fairway rounded-sm p-2 shrink-0">
                          <p className="text-gold font-bold text-xs">{start.toLocaleDateString('en-US', { month: 'short' })}</p>
                          <p className="text-white font-bold text-lg">{start.getDate()}</p>
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="font-display font-semibold text-deep-green line-clamp-1">{event.title}</h3>
                          <p className="text-muted-foreground text-xs">{event.city} | {event.type}{event.entry_fee > 0 ? ` | $${event.entry_fee}` : ' | Free'}</p>
                        </div>
                      </CardContent>
                    </Card>
                  </Link>
                );
              })}
            </div>
          </section>
        )}

        {/* Leagues */}
        {hasLeagues && (
          <section className="mb-10">
            <h2 className="font-display font-bold text-xl text-deep-green mb-4 flex items-center gap-2">
              <Trophy className="w-5 h-5 text-gold" /> Leagues ({guide.leagues.length})
            </h2>
            <div className="grid md:grid-cols-2 gap-4">
              {guide.leagues.map(league => (
                <Card key={league.league_id || league.id} className="bg-white border-0 shadow-sm">
                  <CardContent className="p-5">
                    <h3 className="font-display font-bold text-deep-green mb-1">{league.name}</h3>
                    <p className="text-muted-foreground text-sm mb-2">{league.city}, {league.state}</p>
                    {league.season_info && <p className="text-xs text-muted-foreground">{league.season_info}</p>}
                  </CardContent>
                </Card>
              ))}
            </div>
          </section>
        )}

        {/* Pros */}
        {hasPros && (
          <section className="mb-10">
            <h2 className="font-display font-bold text-xl text-deep-green mb-4 flex items-center gap-2">
              <Award className="w-5 h-5 text-gold" /> Golf Pros ({guide.pros.length})
            </h2>
            <div className="grid md:grid-cols-2 gap-4">
              {guide.pros.map(pro => (
                <Card key={pro.pro_id || pro.id} className="bg-white border-0 shadow-sm">
                  <CardContent className="p-5 flex items-start gap-4">
                    <div className="w-12 h-12 rounded-full bg-fairway flex items-center justify-center shrink-0">
                      <span className="text-gold font-bold">{pro.name?.split(' ').map(n => n[0]).join('')}</span>
                    </div>
                    <div>
                      <h3 className="font-display font-bold text-deep-green">{pro.name}</h3>
                      <p className="text-gold text-sm">{pro.title}</p>
                      <p className="text-muted-foreground text-xs mt-1">{pro.specialties?.join(', ')}</p>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </section>
        )}
      </div>
    </div>
  );
};

export default TravelGuideDetailPage;
